import React, { useState, useCallback } from 'react';
import { style } from 'typestyle';
import { useQueryUserConfig } from '@src/queries/users/useQueryUserConfig';
import { Page } from '@src/components/Page';
import { Pathname } from '@src/types/Browsing';
import { Services } from '@src/types/Services';
import { ADALONG_COLORS } from '@src/styles';
import { ConversationsGrid } from './Grid/ConversationsGrid';
import { MessagesPage } from './Messages/MessagesPage';
import AuthorizedPage from '@src/components/AuthorizedPage';

export function CRMPage(props: { services: Services }) {
  const { data: userConfig } = useQueryUserConfig();
  const user = userConfig?.user ?? null;
  
  const [selectedConversation, setSelectedConversation] = useState<{
    conversationId: string;
    target: string;
    interlocutor: string;
  } | undefined>(undefined);

  const [currentConversationData, setCurrentConversationData] = useState<any>(undefined);

  // Handle conversation selection from grid
  const handleConversationChange = useCallback(
    (targetId: string, interlocutorId: string, conversationId: string) => {
      setSelectedConversation({
        conversationId,
        target: targetId,
        interlocutor: interlocutorId,
      });
    },
    []
  );

  const handleConversationLoad = useCallback((conversationData: any) => {
    setCurrentConversationData(conversationData);
  }, []);

  const handleCloseConversation = useCallback(() => {
    setSelectedConversation(undefined);
    setCurrentConversationData(undefined);
  }, []);

  return (
    <Page path={Pathname.crm}>
      <AuthorizedPage tab="crm" user={user}>
        <div className={contentClass}>

          {/* Main Content */}
          <div className={mainContentClass}>
            {/* Conversations Grid */}
            <div className={conversationsContainerClass(selectedConversation)}>
              <ConversationsGrid 
                onConversationChange={handleConversationChange}
                hasSelectedConversation={!!selectedConversation}
              />
            </div>

            {/* Messages Area - Only show when conversation is selected */}
            {selectedConversation ? (
              <div className={messagesContainerClass}>
                {/* Conversation Banner */}
                <div className={conversationBannerClass}>
                  <div className={bannerContentClass}>
                    <div className={conversationTitleClass}>
                      Conversation with {currentConversationData?.conversationInterlocutor?.name || 
                                       currentConversationData?.interlocutorUsername || 
                                       'Unknown User'}
                    </div>
                    <div className={conversationSubtitleClass}>
                      {currentConversationData?.hasIPR ? 'Rights Request' : 'Direct Message'}
                    </div>
                  </div>
                  <button 
                    className={closeButtonClass}
                    onClick={handleCloseConversation}
                    aria-label="Close conversation"
                  >
                    ✕
                  </button>
                </div>

                <div className={messagesWrapperClass}>
                  <MessagesPage
                    conversation={selectedConversation}
                    services={props.services}
                    onConversationLoad={handleConversationLoad}
                  />
                </div>
              </div>
            ) : null }
          </div>
        </div>
      </AuthorizedPage>
    </Page>
  );
}

  const contentClass = style({
    padding: '0 20px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  });

  const mainContentClass = style({
    flex: 1,
    display: 'flex',
    gap: '20px',
    overflow: 'hidden',
  });

  const conversationsContainerClass = (
    selectedConversation: {
      conversationId: string;
      target: string;
      interlocutor: string;
    } | undefined
  ) => style({
    width: selectedConversation ? '60%' : '100%', // 60% when conversation selected
    transition: 'width 0.3s ease',
    overflow: 'hidden',
  });

  const messagesContainerClass = style({
    width: '40%', 
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: '8px',
    border: `1px solid ${ADALONG_COLORS.LIGHT_GRAY}`,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    backgroundColor: ADALONG_COLORS.SMOOTH_BLACK,
    marginBottom: '20px', 
  });

  const conversationBannerClass = style({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: ADALONG_COLORS.SMOOTH_BLACK, 
  });

  const bannerContentClass = style({
    display: 'flex',
    flexDirection: 'column',
  });

  const conversationTitleClass = style({
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '4px',
  });

  const conversationSubtitleClass = style({
    fontSize: '12px',
    color: ADALONG_COLORS.LIGHT_GRAY,
  });

  const closeButtonClass = style({
    background: 'none',
    border: 'none',
    color:  ADALONG_COLORS.LIGHT_GRAY,
    fontSize: '16px',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.2s ease',
    $nest: {
      '&:hover': {
        color: '#ffffff',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
  });

  const messagesWrapperClass = style({
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0, // Important: allows flex child to shrink
  });